<template>
  <div class="search-header is-results">
    <div class="search-header-left">{{ totalText }}</div>
    <div class="search-header-right">
      <button
        class="button is-primary is-small !h-[39px] tablet:hidden"
        @click="filterOpen = true"
      >
        {{ $texts('search.filterTitle') }}
      </button>
      <div class="sorting">
        <div class="select is-small text-primary">
          <select v-model="sort">
            <option
              v-for="option in sortingOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
    </div>
    <div v-if="hasMap" class="search-header-bottom">
      <NuxtLink :to="showMapLink" class="button is-small">
        <SpriteSymbol name="map" />
        <span>{{ $texts('search.showMap') }}</span>
      </NuxtLink>
    </div>
  </div>
</template>

<script lang="ts" setup>
/*
Original Nuxt 2 props:
props: {
 hasMap: {
   type: Boolean,
   default: false,
 } as PropOptions<boolean>,
},
*/

const props = withDefaults(
  defineProps<{
    hasMap?: boolean
    total: number
  }>(),
  {
    hasMap: false,
  },
)

const filterOpen = defineModel<boolean>('filterOpen')

const route = useRoute()
const router = useRouter()

const showMapLink = computed(() => {
  /* Original:
 return {
   path: this.$route.path,
   query: {
     ...this.$route.query,
     map: 'true',
   },
 }
 */
  return {
    path: route.path,
    query: {
      ...route.query,
      map: 'true',
    },
  }
})

const hasLocationFilter = computed(() => {
  /* Original:
 return !!this.$route.query.location
 */
  return !!route.query.location
})

const { $texts } = useEasyTexts()

const totalText = computed(() => {
  /* Original:
 if (this.total === 1) {
   return this.$store.state.search.translations.totalResultsSingle
 }
 return this.$store.state.search.translations.totalResults.replace(
   '@count',
   this.total
 )
 */
  if (props.total === 1) {
    return $texts('totalResultsSingle')
  }

  return $texts('totalResults').replace(
    '@count',
    props.total.toString(),
  )
})

const sort = computed({
  get() {
    /* Original:
   return getQueryParamString(this.$route.query, 'sort') || 'default'
   */
    return (route.query.sort as string) || 'default'
  },
  set(value: string) {
    /* Original:
   const query = {
     ...this.$route.query,
   }
   if (sort === 'default') {
     delete query.sort
   } else {
     query.sort = sort
   }
   this.$router.push({
     name: this.$route.name || '',
     query,
   })
   */
    const query = { ...route.query }
    if (value === 'default') {
      delete query.sort
    } else {
      query.sort = value
    }
    router.push({
      name: route.name || undefined,
      query,
    })
  },
})

const sortingOptions = computed(() => {
  /* Original:
 const options = [
   {
     id: 'default',
     label: this.$store.state.search.translations.sortRecommended,
   },
 ]
 if (!this.hasLocationFilter) {
   options.push({
     id: 'title',
     label: this.$store.state.search.translations.sortTitle,
   })
 }
 return options
 */
  const options = [
    {
      id: 'default',
      label: $texts('search.sortRecommended'),
    },
  ]
  if (!hasLocationFilter.value) {
    options.push({
      id: 'title',
      label: $texts('search.sortTitle'),
    })
  }
  return options
})
</script>

<style>
@reference '~/assets/css/tailwind.css';
.search-header {
  @apply mb-size-6 flex flex-wrap items-center justify-between border-b-2 border-solid border-border-strong pb-size-6;
  @apply tablet:flex-nowrap;
  min-height: 3.75rem;
}

.search-header-left {
  @apply font-bold desktop:text-size-5;
}
.search-header-right {
  order: 5;
  @apply grid;
  @apply tablet:flex;
  flex: 0 0 100%;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1rem;

  .select,
  .select select {
    @apply w-full text-button-color;

    &:hover {
      @apply border-border-hover;
    }

    &:active,
    &:focus {
      @apply border-button-focus-border-color;
    }
  }

  @variant tablet {
    order: 1;
    margin-left: auto;
    flex: 0 0 auto;

    .button {
      margin: 0;
    }
  }

  .button:not(:last-child) {
    @apply tablet:mr-size-7;
  }

  .button .icon {
    font-size: 1.4rem;
    line-height: 1;
    margin-top: 0.2em;
  }
}

.search-header-bottom {
  @apply mb-size-7 ml-size-7;
  @apply tablet:mb-0;
  order: 2;

  & .button.is-small {
    height: 39px;
  }
}
</style>
